import { useState, useEffect } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { writingAPI, practiceAPI } from '../../api/client'
import { useExamFramework } from '../../hooks/useExamFramework'
import type { WritingSubmission, WritingFigure } from '@enghustle/shared'
import AppLayout from '../../components/layout/AppLayout'
import ChartFigure from '../../components/ChartFigure'

const H = ({ style, ...p }: React.HTMLAttributes<HTMLElement>) => (
  <span style={{ fontFamily: "'Space Grotesk', sans-serif", ...style }} {...p} />
)

// Sample prompts keyed by `${examType}:${taskCode}` (no per-task prompt API yet).
const WRITING_PROMPTS: Record<string, string> = {
  'vstep:task1': 'Write a letter (about 120 words) to a friend inviting them to visit your city. Say what you can do together and why they should come.',
  'vstep:task2': 'Some people believe that technology has made our lives more complicated. To what extent do you agree or disagree?',
  'ielts_academic:task1': 'The chart below shows the percentage of households with internet access in three countries between 2010 and 2020. Summarise the information by selecting and reporting the main features, and make comparisons where relevant.',
  'ielts_academic:task2': 'Some people think universities should focus only on academic subjects; others believe they should prepare students for the workplace. Discuss both views and give your own opinion.',
  'ielts_general:task1': 'You recently bought a product online that arrived damaged. Write a letter to the company. In your letter: explain what you bought, describe the problem, and say what you would like them to do.',
  'ielts_general:task2': 'In many countries people are working longer hours than before. Why is this happening, and what effects does it have on individuals and society?',
}

// Minimum word count by task and exam (FR-029): VSTEP T1 120, IELTS T1 150, T2 250.
function minWordsFor(taskCode: string, examType: string): number {
  if (taskCode === 'task1') return examType === 'vstep' ? 120 : 150
  return 250
}

const CRITERION_LABELS: Record<string, string> = {
  taskAchievement: 'Task Achievement',
  coherenceCohesion: 'Coherence & Cohesion',
  lexicalResource: 'Lexical Resource',
  grammaticalRange: 'Grammatical Range',
}

const ANNOTATION_COLORS: Record<string, { border: string; bg: string; label: string }> = {
  grammar:          { border: '#b41340', bg: 'rgba(180,19,64,0.06)',  label: 'Grammar' },
  lexical:          { border: '#8e00d5', bg: 'rgba(142,0,213,0.06)', label: 'Lexical' },
  cohesion:         { border: '#004be2', bg: 'rgba(0,75,226,0.06)',  label: 'Cohesion' },
  task_achievement: { border: '#b45309', bg: 'rgba(180,83,9,0.06)',  label: 'Task' },
}

/**
 * FR-010 Writing Training Module
 * FR-010a Inline Annotation UI, FR-010b Rewrite Suggestion
 */
export default function WritingPage() {
  const { parts, partLabel, displayName, examType, isLoading } = useExamFramework()
  const writingTasks = parts('writing')
  const [activeCode, setActiveCode] = useState<string>('')
  const activeTask = writingTasks.find(t => t.partCode === activeCode)

  useEffect(() => {
    if (!activeCode && writingTasks.length > 0) setActiveCode(writingTasks[0].partCode)
  }, [writingTasks, activeCode])

  const [text, setText] = useState('')
  const [result, setResult] = useState<WritingSubmission | null>(null)

  const taskCode = activeCode || 'task2'
  // Per-part prompt from the bank (FR-029), falling back to a built-in sample.
  const { data: promptData } = useQuery({
    queryKey: ['writing-prompt', examType, taskCode],
    queryFn: () => practiceAPI.getPrompt('writing', taskCode, examType),
    enabled: !!activeCode,
  })
  const staticPrompt = WRITING_PROMPTS[`${examType}:${taskCode}`] ?? WRITING_PROMPTS['vstep:task2']
  const prompt = (promptData?.source === 'db' && promptData.prompt) ? promptData.prompt : staticPrompt
  const minWords = minWordsFor(taskCode, examType)
  // IELTS Academic Task 1 chart to describe (FR-029-B), only when the prompt carries one.
  const figure: WritingFigure | undefined =
    examType === 'ielts_academic' && taskCode === 'task1' ? promptData?.figure : undefined

  const submit = useMutation({
    mutationFn: () => writingAPI.submit({
      // Backend expects task1/task2; framework writing partCodes are exactly these
      taskType: (taskCode === 'task1' ? 'task1' : 'task2'),
      taskPrompt: prompt,
      text,
      targetLevel: 'B2',
      sessionId: 'placeholder-session-id',
      examType,
    }),
    onSuccess: (data) => setResult(data),
  })

  const rewrite = useMutation({
    mutationFn: () => writingAPI.requestRewrite(result!.id),
  })

  const wordCount = text.trim().split(/\s+/).filter(Boolean).length

  // Reset editor when switching task
  function switchTask(code: string) {
    setActiveCode(code)
    setText('')
    setResult(null)
  }

  return (
    <AppLayout>
      {/* Page header */}
      <div style={{ marginBottom: '2rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
          <span className="material-symbols-outlined" style={{ color: '#8e00d5', fontSize: '1.125rem', fontVariationSettings: "'FILL' 1" }}>edit_note</span>
          <span style={{ fontSize: '0.6875rem', fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#8e00d5' }}>AI Writing Coach</span>
        </div>
        <H style={{ display: 'block', fontSize: 'clamp(1.75rem, 3vw, 2.5rem)', fontWeight: 900, letterSpacing: '-0.03em', marginBottom: '0.5rem' }}>
          AI Writing <span style={{ color: '#004be2', fontStyle: 'italic' }}>Editor</span>
        </H>
        <p style={{ color: '#535b71', fontSize: '1rem', lineHeight: 1.6 }}>
          Phân tích ngôn ngữ chuyên sâu cho bài viết {displayName} của bạn.
        </p>
      </div>

      {/* Task selector (data-driven) */}
      {!result && writingTasks.length > 0 && (
        <div className="scroll-tabs" style={{ display: 'flex', gap: '0.75rem', marginBottom: '2rem' }}>
          {writingTasks.map(task => {
            const sel = activeCode === task.partCode
            return (
              <button
                key={task.partCode}
                onClick={() => switchTask(task.partCode)}
                style={{
                  flex: 1, minWidth: 0, padding: '0.875rem 1.25rem', borderRadius: '1rem',
                  border: sel ? 'none' : '1.5px solid #e8ecf8', cursor: 'pointer', transition: 'all 0.2s', textAlign: 'left',
                  background: sel ? 'linear-gradient(135deg, #8e00d5, #c44dff)' : 'white',
                  boxShadow: sel ? '0 8px 20px rgba(142,0,213,0.25)' : '0 2px 8px rgba(0,0,0,0.04)',
                }}
              >
                <H style={{ display: 'block', fontSize: '0.9375rem', fontWeight: 800, color: sel ? 'white' : '#272e42', lineHeight: 1.3 }}>{partLabel(task)}</H>
                <span style={{ fontSize: '0.75rem', color: sel ? 'rgba(255,255,255,0.7)' : '#a5adc6', fontWeight: 600 }}>≥{minWordsFor(task.partCode, examType)} từ</span>
              </button>
            )
          })}
        </div>
      )}

      {isLoading && !result ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '4rem' }}><div className="spinner" /></div>
      ) : !result ? (
        <WritingEditor
          taskLabel={activeTask ? partLabel(activeTask) : 'Task'}
          prompt={prompt}
          figure={figure}
          text={text}
          onChange={setText}
          wordCount={wordCount}
          minWords={minWords}
          onSubmit={() => submit.mutate()}
          isSubmitting={submit.isPending}
          error={submit.isError ? (submit.error as Error).message : undefined}
        />
      ) : (
        <WritingResult
          submission={result}
          onRequestRewrite={() => rewrite.mutate()}
          rewrite={rewrite.data?.rewrite}
          isRewriting={rewrite.isPending}
          onReset={() => { setResult(null); setText('') }}
        />
      )}
    </AppLayout>
  )
}

// ─── Writing Editor ───────────────────────────────────────────────────────────

function WritingEditor({ taskLabel, prompt, figure, text, onChange, wordCount, minWords, onSubmit, isSubmitting, error }: {
  taskLabel: string; prompt: string; figure?: WritingFigure; text: string; onChange: (t: string) => void
  wordCount: number; minWords: number
  onSubmit: () => void; isSubmitting: boolean; error?: string
}) {
  const pct = Math.min(100, (wordCount / minWords) * 100)
  const isEnough = wordCount >= minWords

  return (
    <div className="drill-2col" style={{ alignItems: 'start' }}>
      {/* Editor panel */}
      <div style={{ background: 'white', borderRadius: '1.5rem', overflow: 'hidden', boxShadow: '0 10px 30px rgba(0,75,226,0.06)' }}>
        {/* Prompt */}
        <div style={{ padding: '1.5rem 2rem', background: '#eef0ff', borderBottom: 'none' }}>
          <span style={{ fontSize: '0.6875rem', fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#004be2', display: 'block', marginBottom: '0.5rem' }}>{taskLabel} — Đề bài</span>
          <p style={{ color: '#272e42', fontSize: '0.9375rem', lineHeight: 1.7, fontWeight: 500 }}>{prompt}</p>
          {figure && (
            <div style={{ marginTop: '1rem' }}>
              <ChartFigure figure={figure} />
            </div>
          )}
        </div>

        {/* Textarea */}
        <div style={{ padding: '1.5rem 2rem' }}>
          <textarea
            value={text}
            onChange={e => onChange(e.target.value)}
            placeholder={`Viết bài của bạn tại đây... (ít nhất ${minWords} từ)`}
            rows={18}
            style={{
              width: '100%', border: 'none', outline: 'none', resize: 'none',
              fontFamily: "'Manrope', sans-serif", fontSize: '1rem', lineHeight: 1.8,
              color: '#272e42', background: 'transparent',
            }}
          />
        </div>

        {/* Footer */}
        <div style={{ padding: '1rem 2rem', background: '#f8f9fe', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            {/* Word count ring */}
            <div style={{ position: 'relative', width: '3rem', height: '3rem', flexShrink: 0 }}>
              <svg width="48" height="48" style={{ transform: 'rotate(-90deg)' }}>
                <circle cx="24" cy="24" r="20" fill="none" stroke="#d1dcff" strokeWidth="3" />
                <circle cx="24" cy="24" r="20" fill="none"
                  stroke={isEnough ? '#004be2' : '#8e00d5'}
                  strokeWidth="3"
                  strokeDasharray={`${2 * Math.PI * 20}`}
                  strokeDashoffset={`${2 * Math.PI * 20 * (1 - pct / 100)}`}
                  strokeLinecap="round"
                  style={{ transition: 'stroke-dashoffset 0.4s' }}
                />
              </svg>
              <span style={{
                position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontFamily: "'Space Grotesk', sans-serif", fontWeight: 800, fontSize: '0.625rem',
                color: isEnough ? '#004be2' : '#8e00d5',
              }}>{wordCount}</span>
            </div>
            <div>
              <span style={{ display: 'block', fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700, fontSize: '0.875rem', color: isEnough ? '#004be2' : '#8e00d5' }}>
                {wordCount} / {minWords} từ
              </span>
              <span style={{ fontSize: '0.75rem', color: '#535b71' }}>
                {isEnough ? '✓ Đủ số từ' : `Cần thêm ${minWords - wordCount} từ`}
              </span>
            </div>
          </div>

          <button
            onClick={onSubmit}
            disabled={isSubmitting || wordCount < 50}
            style={{
              padding: '0.875rem 2rem', borderRadius: '9999px', border: 'none',
              cursor: isSubmitting || wordCount < 50 ? 'not-allowed' : 'pointer',
              background: isSubmitting || wordCount < 50 ? 'rgba(0,75,226,0.3)' : 'linear-gradient(135deg, #004be2, #809bff)',
              color: 'white', fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700, fontSize: '0.9375rem',
              display: 'flex', alignItems: 'center', gap: '0.625rem', transition: 'all 0.2s',
            }}
          >
            {isSubmitting ? (
              <><span className="spinner" style={{ width: '1rem', height: '1rem', borderColor: 'rgba(255,255,255,0.3)', borderTopColor: 'white' }} /> Đang chấm...</>
            ) : (
              <><span className="material-symbols-outlined" style={{ fontSize: '1.125rem', fontVariationSettings: "'FILL' 1" }}>psychology</span> Nộp bài / Chấm điểm</>
            )}
          </button>
        </div>
      </div>

      {/* Side tips */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        {/* Annotation legend */}
        <div style={{ background: 'white', borderRadius: '1.5rem', padding: '1.5rem', boxShadow: '0 8px 24px rgba(0,0,0,0.04)' }}>
          <H style={{ display: 'block', fontSize: '0.9375rem', fontWeight: 800, marginBottom: '1rem' }}>Chú thích màu sắc</H>
          {Object.entries(ANNOTATION_COLORS).map(([type, cfg]) => (
            <div key={type} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.625rem' }}>
              <div style={{ width: '2rem', height: '0.1875rem', borderBottom: `2px wavy ${cfg.border}` }} />
              <span style={{ fontSize: '0.8125rem', fontWeight: 600, color: '#535b71' }}>{cfg.label}</span>
            </div>
          ))}
        </div>

        {/* Tips */}
        <div style={{ background: '#eef0ff', borderRadius: '1.5rem', padding: '1.5rem' }}>
          <H style={{ display: 'block', fontSize: '0.9375rem', fontWeight: 800, marginBottom: '1rem', color: '#004be2' }}>Mẹo viết Task 2</H>
          {[
            'Giới thiệu quan điểm rõ ràng ngay đoạn đầu',
            'Mỗi thân bài tập trung 1 luận điểm + ví dụ',
            'Dùng từ nối đa dạng (Furthermore, However...)',
            'Kết bài paraphrase lại thesis statement',
          ].map((tip, i) => (
            <div key={i} style={{ display: 'flex', gap: '0.625rem', marginBottom: '0.75rem' }}>
              <span style={{ flexShrink: 0, width: '1.375rem', height: '1.375rem', borderRadius: '50%', background: '#004be2', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.6875rem', fontWeight: 800 }}>{i + 1}</span>
              <span style={{ fontSize: '0.8125rem', lineHeight: 1.5, color: '#535b71' }}>{tip}</span>
            </div>
          ))}
        </div>

        {error && (
          <div style={{ padding: '1rem', borderRadius: '1rem', background: 'rgba(180,19,64,0.08)', color: '#b41340', fontSize: '0.875rem', fontWeight: 600 }}>
            {error}
          </div>
        )}
      </div>
    </div>
  )
}

// ─── Writing Result ───────────────────────────────────────────────────────────

function WritingResult({ submission, onRequestRewrite, rewrite, isRewriting, onReset }: {
  submission: WritingSubmission
  onRequestRewrite: () => void
  rewrite?: string
  isRewriting: boolean
  onReset: () => void
}) {
  const [activeAnnotation, setActiveAnnotation] = useState<string | null>(null)

  const scores = submission.scores
  const criteriaEntries = scores
    ? Object.entries(scores).filter(([k]) => k !== 'overall')
    : []

  return (
    <div className="drill-2col" style={{ alignItems: 'start' }}>
      {/* Left: annotated text */}
      <div>
        {/* Score header */}
        <div style={{ background: 'white', borderRadius: '1.5rem', padding: '2rem', marginBottom: '1.5rem', boxShadow: '0 10px 30px rgba(0,75,226,0.06)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1.5rem' }}>
          <div>
            <span style={{ fontSize: '0.6875rem', fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#535b71', display: 'block', marginBottom: '0.25rem' }}>Điểm tổng</span>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.5rem' }}>
              <H style={{ fontSize: '3rem', fontWeight: 900, color: '#004be2', lineHeight: 1 }}>{scores?.overall?.toFixed(1) ?? '—'}</H>
              <H style={{ fontSize: '1.25rem', fontWeight: 500, color: '#535b71' }}>/10</H>
            </div>
            <span style={{ fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700, color: '#8e00d5', fontSize: '0.875rem' }}>
              {submission.predictedLevel}
            </span>
          </div>

          {/* Score neural nodes */}
          <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap' }}>
            {criteriaEntries.map(([key, val]) => (
              <div key={key} style={{ textAlign: 'center' }}>
                <span style={{ display: 'block', fontSize: '0.6875rem', fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#535b71', marginBottom: '0.25rem' }}>
                  {CRITERION_LABELS[key]?.split(' ')[0] ?? key}
                </span>
                <H style={{ fontSize: '1.5rem', fontWeight: 900, color: '#272e42' }}>{(val as number).toFixed(1)}</H>
              </div>
            ))}
          </div>

          <button
            onClick={onReset}
            style={{ padding: '0.75rem 1.5rem', borderRadius: '9999px', border: '2px solid #d1dcff', background: 'transparent', color: '#535b71', fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700, fontSize: '0.875rem', cursor: 'pointer' }}
          >
            Viết lại bài mới
          </button>
          {/* G-5: PDF Export */}
          <button
            onClick={() => writingAPI.exportPdf(submission.id).catch(() => {})}
            style={{ padding: '0.75rem 1.5rem', borderRadius: '9999px', border: '2px solid #d1dcff', background: 'transparent', color: '#8e00d5', fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700, fontSize: '0.875rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.375rem' }}
            title="Xuất bài viết + feedback dạng HTML (in thành PDF)"
          >
            <span className="material-symbols-outlined" style={{ fontSize: '1rem' }}>picture_as_pdf</span>
            Xuất PDF
          </button>
        </div>

        {/* Annotated text */}
        <div style={{ background: 'white', borderRadius: '1.5rem', padding: '2rem', boxShadow: '0 10px 30px rgba(0,75,226,0.06)' }}>
          <H style={{ display: 'block', fontSize: '1rem', fontWeight: 800, marginBottom: '1.25rem', color: '#535b71' }}>Bài viết có chú thích — nhấn để xem giải thích</H>
          <div style={{ fontSize: '1rem', lineHeight: 1.85, color: '#272e42', position: 'relative' }}>
            <InlineAnnotatedText
              text={submission.originalText}
              annotations={submission.annotations}
              activeId={activeAnnotation}
              onSelect={setActiveAnnotation}
            />
          </div>
        </div>

        {/* Rewrite panel */}
        {submission.rewriteTriggered && !rewrite && (
          <div style={{ marginTop: '1.5rem', padding: '1.5rem', background: '#eef0ff', borderRadius: '1.5rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap' }}>
            <div>
              <H style={{ display: 'block', fontWeight: 800, marginBottom: '0.25rem' }}>Gợi ý viết lại</H>
              <p style={{ color: '#535b71', fontSize: '0.875rem' }}>AI phát hiện khoảng cách điểm lớn. Xem gợi ý cải thiện?</p>
            </div>
            <button
              onClick={onRequestRewrite}
              disabled={isRewriting}
              style={{
                padding: '0.875rem 1.5rem', borderRadius: '9999px', border: 'none',
                background: 'linear-gradient(135deg, #004be2, #809bff)', color: 'white',
                fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700, cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: '0.5rem',
              }}
            >
              {isRewriting ? <><span className="spinner" style={{ width: '1rem', height: '1rem', borderColor: 'rgba(255,255,255,0.3)', borderTopColor: 'white' }} /> Đang tạo...</> : <>💡 Xem gợi ý {submission.targetLevel}</>}
            </button>
          </div>
        )}

        {rewrite && (
          <div style={{ marginTop: '1.5rem', background: 'white', borderRadius: '1.5rem', overflow: 'hidden', boxShadow: '0 10px 30px rgba(0,0,0,0.04)' }}>
            <div style={{ padding: '1rem 1.5rem', background: 'rgba(180,83,9,0.06)', display: 'flex', alignItems: 'center', gap: '0.625rem' }}>
              <span className="material-symbols-outlined" style={{ color: '#b45309', fontSize: '1.125rem', fontVariationSettings: "'FILL' 1" }}>warning</span>
              <span style={{ color: '#b45309', fontSize: '0.8125rem', fontWeight: 700 }}>
                Đây là công cụ học tập — không phải bài để nộp. Tự viết lại mới giúp bạn tiến bộ.
              </span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0' }}>
              <div style={{ padding: '1.5rem', borderRight: '1px solid #eef0ff' }}>
                <H style={{ display: 'block', fontSize: '0.875rem', fontWeight: 800, color: '#535b71', marginBottom: '1rem' }}>Bài gốc của bạn</H>
                <p style={{ fontSize: '0.9375rem', lineHeight: 1.75, color: '#272e42' }}>{submission.originalText}</p>
              </div>
              <div style={{ padding: '1.5rem' }}>
                <H style={{ display: 'block', fontSize: '0.875rem', fontWeight: 800, color: '#004be2', marginBottom: '1rem' }}>Gợi ý {submission.targetLevel} của AI</H>
                <p style={{ fontSize: '0.9375rem', lineHeight: 1.75, color: '#272e42' }}>{rewrite}</p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Right: score breakdown + active annotation */}
      <div style={{ position: 'sticky', top: '5rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        {/* Score breakdown */}
        <div style={{ background: 'white', borderRadius: '1.5rem', padding: '1.5rem', boxShadow: '0 8px 24px rgba(0,0,0,0.04)' }}>
          <H style={{ display: 'block', fontSize: '0.9375rem', fontWeight: 800, marginBottom: '1.25rem' }}>Điểm chi tiết</H>
          {criteriaEntries.map(([key, val]) => {
            const pct = Math.min(100, ((val as number) / 10) * 100)
            return (
              <div key={key} style={{ marginBottom: '1.25rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.375rem' }}>
                  <span style={{ fontSize: '0.8125rem', fontWeight: 600, color: '#535b71' }}>{CRITERION_LABELS[key] ?? key}</span>
                  <H style={{ fontSize: '0.875rem', fontWeight: 800, color: '#004be2' }}>{(val as number).toFixed(1)}</H>
                </div>
                <div style={{ height: '0.375rem', background: '#eef0ff', borderRadius: '9999px', overflow: 'hidden' }}>
                  <div style={{ width: `${pct}%`, height: '100%', background: 'linear-gradient(90deg, #004be2, #809bff)', borderRadius: '9999px', transition: 'width 0.6s' }} />
                </div>
              </div>
            )
          })}
        </div>

        {/* Active annotation detail */}
        {activeAnnotation && (() => {
          const ann = submission.annotations.find(a => a.id === activeAnnotation)
          if (!ann) return null
          const cfg = ANNOTATION_COLORS[ann.type] ?? ANNOTATION_COLORS.grammar
          return (
            <div style={{ background: 'white', borderRadius: '1.5rem', padding: '1.5rem', boxShadow: '0 8px 24px rgba(0,0,0,0.04)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                <span style={{ padding: '0.25rem 0.75rem', borderRadius: '9999px', background: cfg.bg, color: cfg.border, fontSize: '0.6875rem', fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                  {cfg.label}
                </span>
                <button onClick={() => setActiveAnnotation(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#a5adc6' }}>
                  <span className="material-symbols-outlined" style={{ fontSize: '1.125rem' }}>close</span>
                </button>
              </div>
              <p style={{ fontSize: '0.875rem', color: '#535b71', lineHeight: 1.6, marginBottom: '1rem' }}>{ann.explanation}</p>
              {ann.suggestedText && (
                <div style={{ padding: '0.875rem', background: '#eef0ff', borderRadius: '0.875rem' }}>
                  <span style={{ fontSize: '0.6875rem', fontWeight: 700, color: '#004be2', textTransform: 'uppercase', letterSpacing: '0.08em', display: 'block', marginBottom: '0.375rem' }}>Gợi ý</span>
                  <p style={{ fontSize: '0.875rem', fontWeight: 600, color: '#272e42' }}>{ann.suggestedText}</p>
                </div>
              )}
            </div>
          )
        })()}

        {/* Annotation legend */}
        <div style={{ background: '#eef0ff', borderRadius: '1.5rem', padding: '1.5rem' }}>
          <H style={{ display: 'block', fontSize: '0.875rem', fontWeight: 800, marginBottom: '1rem' }}>Chú thích màu</H>
          {Object.entries(ANNOTATION_COLORS).map(([type, cfg]) => (
            <div key={type} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
              <div style={{ width: '2rem', borderBottom: `2px wavy ${cfg.border}`, flexShrink: 0 }} />
              <span style={{ fontSize: '0.8125rem', color: '#535b71', fontWeight: 600 }}>{cfg.label}</span>
            </div>
          ))}
          <p style={{ fontSize: '0.75rem', color: '#a5adc6', marginTop: '0.75rem' }}>Nhấn vào chú thích để xem chi tiết</p>
        </div>
      </div>
    </div>
  )
}

// ─── Inline Annotation ────────────────────────────────────────────────────────

function InlineAnnotatedText({ text, annotations, activeId, onSelect }: {
  text: string
  annotations: WritingSubmission['annotations']
  activeId: string | null
  onSelect: (id: string | null) => void
}) {
  if (!annotations.length) return <>{text}</>

  const sorted = [...annotations].sort((a, b) => a.startChar - b.startChar)
  const segments: JSX.Element[] = []
  let cursor = 0

  for (const ann of sorted) {
    if (ann.startChar > cursor) {
      segments.push(<span key={`plain-${cursor}`}>{text.slice(cursor, ann.startChar)}</span>)
    }
    const cfg = ANNOTATION_COLORS[ann.type] ?? ANNOTATION_COLORS.grammar
    const isActive = activeId === ann.id
    segments.push(
      <span
        key={ann.id}
        onClick={() => onSelect(isActive ? null : ann.id)}
        style={{
          borderBottom: `2px wavy ${cfg.border}`,
          background: isActive ? cfg.bg : 'transparent',
          cursor: 'pointer',
          borderRadius: '0.125rem',
          transition: 'background 0.2s',
          padding: '0 0.1rem',
        }}
        title={ann.explanation}
      >
        {text.slice(ann.startChar, ann.endChar)}
      </span>,
    )
    cursor = ann.endChar
  }
  if (cursor < text.length) segments.push(<span key="plain-end">{text.slice(cursor)}</span>)

  return <>{segments}</>
}
